import IconClose from "@/assets/icons/ic-close.svg";
import IconHelp from "@/assets/icons/icon-help.svg";
import { SearchInput } from "@/components/common/search-input";
import { ConfirmDialog } from "@/components/common/confirm-dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import Pagination from "@/components/ui/pagination";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { TruncatedTooltip } from "@/components/ui/truncated-tooltip";
import { useAdminPageTitle } from "@/contexts/admin-page-title-context";
import { toast } from "@/hooks/use-toast";
import { formatDate } from "@/lib/utils";
import {
  GetListPrjReq,
  ProjectRes,
  ProjectService,
  ProjectStatus,
} from "@/services/project-service";
import { useCallback, useEffect, useRef, useState } from "react";
import IcNodata from "@/assets/icons/ic-nodata.svg";
import { useNavigate } from "react-router-dom";
import { usePermissions } from "@/lib/permissions";

const STATUS_CONFIG: Record<
  ProjectStatus,
  { label: string; bg: string; text: string; padding: string }
> = {
  in_progress: {
    label: "진행 중",
    bg: "bg-[#FFAA72]",
    text: "text-[#000000]",
    padding: "px-[12.5px]",
  },
  under_review: {
    label: "검토 중",
    bg: "bg-[#E0B244]",
    text: "text-[#000000]",
    padding: "px-[12.5px]",
  },
  completed: {
    label: "완료",
    bg: "bg-[#43946C]",
    text: "text-[#000000]",
    padding: "px-[21px]",
  },
  deleted: {
    label: "삭제됨",
    bg: "bg-[#EBEBEB]",
    text: "text-[#000000]",
    padding: "px-[14.5px]",
  },
};

const STATUS_FILTER_OPTIONS = [
  { value: "all", label: "전체 상태" },
  { value: "in_progress", label: "진행 중" },
  { value: "under_review", label: "검토 중" },
  { value: "completed", label: "완료" },
  { value: "deleted", label: "삭제됨" },
];

const PAGE_SIZE = 10;

const ProjectReportListPage = () => {
  const navigate = useNavigate();
  const { setPageTitle } = useAdminPageTitle();
  const { can } = usePermissions();
  const canUpdate = can.update("project_management");
  const canDelete = can.delete("project_management");

  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState<ProjectStatus | "all">("all");
  const [currentPage, setCurrentPage] = useState(1);
  const [projects, setProjects] = useState<ProjectRes[]>([]);
  const [total, setTotal] = useState(0);
  const [isInitialLoading, setIsInitialLoading] = useState(true);
  const [isSearching, setIsSearching] = useState(false);
  const hasFetchedRef = useRef(false);
  const [deleteTargetId, setDeleteTargetId] = useState<string | null>(null);

  const totalPages = Math.ceil(total / PAGE_SIZE);

  // Debounce search query
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(search.trim());
      setCurrentPage(1);
    }, 300);
    return () => clearTimeout(timer);
  }, [search]);

  const fetchProjects = useCallback(async () => {
    if (hasFetchedRef.current) {
      setIsSearching(true);
    }
    try {
      const params: GetListPrjReq = {
        page: currentPage,
        page_size: PAGE_SIZE,
      };
      if (debouncedSearch) {
        params.search = debouncedSearch;
      }
      if (statusFilter !== "all") {
        params.status = statusFilter as ProjectStatus;
      }
      const res = await ProjectService.getListProject(params);
      if (res.data?.success && res.data?.data) {
        setProjects(res.data.data.items);
        setTotal(res.data.data.total);
      }
    } catch {
      setProjects([]);
      setTotal(0);
    } finally {
      setIsInitialLoading(false);
      setIsSearching(false);
      hasFetchedRef.current = true;
    }
  }, [currentPage, debouncedSearch, statusFilter]);

  useEffect(() => {
    setPageTitle("프로젝트 관리");
  }, [setPageTitle]);

  useEffect(() => {
    fetchProjects();
  }, [fetchProjects]);

  // Status filter change
  const handleStatusChange = (value: string) => {
    setStatusFilter(value as ProjectStatus | "all");
    setCurrentPage(1);
  };

  // Page change
  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const handleClearSearch = () => {
    setSearch("");
    setDebouncedSearch("");
    setCurrentPage(1);
  };

  const handleDeleteClick = (e: React.MouseEvent, projectId: string) => {
    e.stopPropagation();
    setDeleteTargetId(projectId);
  };

  const handleDeleteConfirm = async () => {
    if (!deleteTargetId) return;
    try {
      await ProjectService.deleteProject(deleteTargetId);
      toast({ description: "프로젝트가 삭제되었습니다." });
      fetchProjects();
    } catch {
      toast({
        description: "프로젝트 삭제에 실패했습니다.",
        variant: "destructive",
      });
    } finally {
      setDeleteTargetId(null);
    }
  };

  const handleRowClick = (projectId: string) => {
    navigate(`/projects/${projectId}?mode=view`);
  };

  return (
    <>
      {isInitialLoading && (
        <div className="flex items-center justify-center py-20 h-[60vh] w-full">
          <div className="h-8 w-8 animate-spin rounded-full border-4 border-black/10 border-t-[#FF3B00]" />
        </div>
      )}
      <div 
        className="bg-white rounded-[16px]  flex flex-col gap-6"
        style={{ display: isInitialLoading ? "none" : "flex" }}
      >
      {/* Toolbar */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          {/* Search */}
          <SearchInput
            placeholder="이름, 제작자 또는 에이전트로 검색..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            onClear={handleClearSearch}
          />

          {/* Status Filter */}
          <Select value={statusFilter} onValueChange={handleStatusChange}>
            <SelectTrigger className="w-[127px]">
              <SelectValue placeholder="전체 상태" />
            </SelectTrigger>
            <SelectContent>
              {STATUS_FILTER_OPTIONS.map((opt) => (
                <SelectItem key={opt.value} value={opt.value}>
                  {opt.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Record Count */}
      <div className="flex justify-start">
        <span className="text-sm text-[#8D8D8D]">
          총 {total}개 프로젝트 중 {projects.length}개 표시 중
        </span>
      </div>

      {/* Table - Figma: node 1412:13705 */}
      <div
        className={`border border-[rgba(0,0,0,0.09)] rounded overflow-x-auto min-w-0 w-full transition-opacity duration-200 ${isSearching ? "pointer-events-none" : ""}`}
      >
        <Table className="min-w-[1050px]">
          <TableHeader>
            <TableRow className="bg-[#EBEBEB]">
              <TableHead className="w-[300px]">프로젝트명</TableHead>
              <TableHead className="w-[152px]">등록자</TableHead>
              <TableHead className="w-[121px]">생성일</TableHead>
              <TableHead className="w-[121px]">상태</TableHead>
              <TableHead className="w-[162px]">보고서 열람</TableHead>
              <TableHead className="w-[180px] text-right">관리</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {projects.map((project) => {
              const statusConfig = STATUS_CONFIG[project.status] || STATUS_CONFIG.in_progress;
              return (
                <TableRow
                  key={project.id}
                  // className="cursor-pointer"
                  // onClick={() => handleRowClick(project.id)}
                >
                  <TableCell className="text-[#000000] max-w-[300px] truncate py-3">
                    <TruncatedTooltip className="block truncate text-base  text-[#000000]">
                      {project.name}
                    </TruncatedTooltip>
                  </TableCell>
                  <TableCell className="text-base text-[#000000e8] py-3 max-w-[150px] truncate">
                    <TruncatedTooltip className="block truncate text-base  text-[#000000]">
                      {project.creator_name}
                    </TruncatedTooltip>
                  </TableCell>
                  <TableCell className="text-base text-[#000000] whitespace-nowrap py-3 w-[180px]">
                    {formatDate(project.created_at, "YYYY-MM-DD HH:mm:ss")}
                  </TableCell>
                  <TableCell className="py-3 w-[112px] shrink-0">
                    <div
                      className={`inline-flex items-center gap-1 h-6 rounded ${statusConfig.bg} ${statusConfig.text} ${statusConfig.padding}`}
                    >
                      <span className="text-sm font-medium">{statusConfig.label}</span>
                    </div>
                  </TableCell>
                  <TableCell className="py-3">
                    <button
                      className="text-sm font-bold text-[#000000] underline"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleRowClick(project.id);
                      }}
                    >
                      보기
                    </button>
                  </TableCell>
                  <TableCell className="py-3">
                    <div className="flex items-center justify-end gap-[8px]">
                      {canDelete && (
                        <Button
                          variant="ghost"
                          size="icon"
                          className="w-[80px] h-[32px] rounded-[8px] text-[12px] font-bold text-[#000000] bg-[#F7DDCF]"
                          onClick={(e) => handleDeleteClick(e, project.id)}
                        >
                          삭제
                        </Button>
                      )}
                      {canUpdate &&
                      project.status !== "completed" &&
                      project.status !== "deleted" ? (
                        <Button
                          variant="ghost"
                          size="icon"
                          className="w-[80px] h-[32px] rounded-[8px] text-[12px] font-bold text-[#000000] bg-[#EBEBEB]"
                          onClick={(e) => {
                            e.stopPropagation();
                            navigate(`/projects/${project.id}?mode=edit`);
                          }}
                        >
                          수정
                        </Button>
                      ) : (
                        <div className="w-[80px]" />
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              );
            })}
            {projects.length === 0 && !isInitialLoading && !isSearching && (
              <TableRow>
                <TableCell colSpan={6} className="h-[600px] text-center">
                  <div className="flex flex-col items-center justify-center h-full gap-4">
                    <img src={IcNodata} alt="No data" />
                    <p className="text-[32px] text-[#000000E8]">
                      {search || statusFilter !== "all" ? "검색 결과가 없습니다" : "No data"}
                    </p>
                    <p className="text-base text-[#000000]">
                      {search || statusFilter !== "all" ? "" : "아직 생성된 프로젝트가 없습니다"}
                    </p>
                  </div>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>

      {/* Pagination */}
      {!isInitialLoading && projects.length > 0 && (
        <Pagination
          className="justify-center"
          currentPage={currentPage}
          totalPages={totalPages}
          onPageChange={handlePageChange}
        />
      )}

      <ConfirmDialog
        iconVariant="neutral"
        open={deleteTargetId !== null}
        onOpenChange={(open) => !open && setDeleteTargetId(null)}
        title="해당 프로젝트를 삭제하시겠습니까?"
        description="삭제 후 복구할 수 없습니다."
        primaryAction={{ label: "네", onClick: handleDeleteConfirm }}
        secondaryAction={{
          label: "아니요",
          onClick: () => setDeleteTargetId(null),
        }}
        icon={<img src={IconHelp} alt="Icon Alert Circle" />}
      />
    </div>
    </>
  );
};

export default ProjectReportListPage;
